/**
 * Formatting and state-normalisation helpers for the payment detail mapper.
 */
import { ApiBottleneck } from './paymentDetailApi';
import { FlowState, Direction } from './paymentDetailTypes';

const parse = (iso?: string | null): Date | null => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** ISO → "YYYY-MM-DD HH:mm:ss UTC"; '—' when missing, raw string when unparseable. */
export const fullTimestamp = (iso?: string | null): string => {
  if (!iso) return '—';
  const date = parse(iso);
  return date ? `${date.toISOString().substring(0, 19).replace('T', ' ')} UTC` : iso;
};

/** "OUTBOUND" → "Outbound". */
export const titleCase = (value: string): string => value.charAt(0) + value.slice(1).toLowerCase();

export const toDirection = (value?: string | null): Direction =>
  (value ?? '').toUpperCase() === 'INBOUND' ? 'INBOUND' : 'OUTBOUND';

/** Backend state vocabulary → the UI's five FlowStates. */
export const toFlowState = (state?: string | null): FlowState => {
  switch ((state ?? '').toUpperCase()) {
    case 'SUCCESS':
    case 'SUCCEEDED':
    case 'COMPLETED':
    case 'SETTLED':
    case 'NOT_HELD': // OUTAGE check passed — payment was not held
      return 'SUCCESS';
    case 'FAILED':
    case 'FAILURE':
    case 'ERROR':
    case 'REJECTED':
      return 'FAILED';
    case 'RECEIVED':
      return 'RECEIVED';
    case 'HELD': // OUTAGE check held the payment
    case 'IN_OUTAGE':
    case 'OUTAGE':
      return 'IN_OUTAGE';
    default:
      return 'PROCESSING';
  }
};

/** ISO → "HH:mm:ss.SSS" UTC; '-' when missing, raw string when unparseable. */
export const timeOfDay = (iso?: string | null): string => {
  if (!iso) return '-';
  const date = parse(iso);
  return date ? date.toISOString().substring(11, 23) : iso;
};

/**
 * Duration in ms → the largest two units, e.g. "2d 7h" / "1h 36m" /
 * "12m 5s" / "45s" / "820ms". Returns undefined when absent so callers can
 * omit the value rather than print a placeholder.
 */
export const formatMs = (ms?: number | null): string | undefined => {
  if (ms == null || Number.isNaN(ms)) return undefined;
  if (ms < 1000) return `${Math.round(ms)}ms`;
  const totalSeconds = Math.round(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

/** 0.0–1.0 → "76%"; undefined when absent. */
export const percent = (share?: number | null): string | undefined =>
  share == null || Number.isNaN(share) ? undefined : `${Math.round(share * 100)}%`;

/**
 * A bottleneck reads as a system, or as the handoff between two systems:
 * "BIP_V1 · 2m 29s · 82%" / "PAYSTATION → BIP_V1 · 7s · 76%". Shared by the
 * leg-wide summary and each graph's own summary.
 */
export const bottleneckLabel = (bottleneck?: ApiBottleneck): string | undefined => {
  if (!bottleneck) return undefined;
  const where = bottleneck.system
    ?? [bottleneck.fromSystem, bottleneck.toSystem].filter(Boolean).join(' → ');
  return [where || undefined, formatMs(bottleneck.latencyMs), percent(bottleneck.percentOfTotal)]
    .filter(Boolean).join(' · ') || undefined;
};
